import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { IMG } from "../data/images";
import { Reveal, Overline } from "./Reveal";
import { useLang } from "../i18n";

const DISHES = [
    { name: "Butter Chicken", img: IMG.butterChicken, testId: "dish-butter-chicken" },
    { name: "Chicken Tikka Masala", img: IMG.tikkaMasala, testId: "dish-chicken-tikka-masala" },
    { name: "Paneer Dishes", img: IMG.paneer, testId: "dish-paneer" },
    { name: "Dal", img: IMG.dal, testId: "dish-dal" },
    { name: "Biryani", img: IMG.biryani, testId: "dish-biryani" },
    { name: "Tandoor Specials", img: IMG.tandoori, testId: "dish-tandoor" },
];

export const Favourites = () => {
    const { t } = useLang();
    const descs = t("favourites.descs");
    const navigate = useNavigate();
    const goMenu = () => {
        navigate("/menu");
        window.__lenis?.scrollTo(0, { immediate: true });
    };

    return (
        <section id="favourites" data-testid="royal-favourites" className="relative py-24 sm:py-32 bg-onyx/40">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
                    <div>
                        <Overline>{t("favourites.overline")}</Overline>
                        <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-cream leading-[1.05]">
                            {t("favourites.h1a")} <span className="italic gold-text">{t("favourites.h1b")}</span>
                        </h2>
                    </div>
                    <button
                        data-testid="favourites-view-full-menu"
                        onClick={goMenu}
                        className="group inline-flex items-center gap-2 text-sm tracking-[0.15em] uppercase text-saffron-glow hover:text-cream transition-colors duration-300"
                    >
                        {t("favourites.viewFull")}
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                </Reveal>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {DISHES.map((d, i) => (
                        <Reveal key={d.name} delay={0.08 * (i % 3)}>
                            <motion.article
                                data-testid={d.testId}
                                whileHover="hover"
                                className="group relative rounded-2xl overflow-hidden gold-border bg-elevated/60 cursor-pointer hover:shadow-[0_20px_60px_rgba(217,119,6,0.12)] transition-shadow duration-500"
                                onClick={goMenu}
                            >
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <motion.img
                                        src={d.img}
                                        alt={d.name}
                                        loading="lazy"
                                        variants={{ hover: { scale: 1.08 } }}
                                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                                </div>
                                <div className="p-6">
                                    <h3 className="font-serif text-2xl text-cream group-hover:text-saffron-glow transition-colors duration-500">
                                        {d.name}
                                    </h3>
                                    <p className="mt-2 text-sm text-stone-400 font-light leading-relaxed">{descs[i]}</p>
                                    <span className="mt-4 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-brass group-hover:text-saffron-glow transition-colors duration-300">
                                        {t("favourites.viewMenu")}
                                        <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                                    </span>
                                </div>
                            </motion.article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};
